import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Sofa, Leaf, Search, Filter, ClipboardList, Clock, CheckCircle2, Truck, Download, BookOpen, History, Phone, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminMapView from "@/components/admin/AdminMapView";
import PuntoLimpioPanel from "@/components/admin/puntolimpio/PuntoLimpioPanel";
import BonificacionPanel from "@/components/admin/bonificacion/BonificacionPanel";
import HaciendaPanel from "@/components/admin/hacienda/HaciendaPanel";
import IslasPanel from "@/components/admin/islas/IslasPanel";
import SolicitudTelefonoDialog from "@/components/admin/SolicitudTelefonoDialog";
import MfaSettings from "@/components/admin/MfaSettings";
import { ESTADOS, ESTADO_COLORS } from "@/mock";
import { adminListSolicitudes, adminUpdateSolicitud, adminStats, adminExportSolicitudes, adminMe, adminHistorialSolicitud } from "@/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [statsData, setStatsData] = useState({ total: 0, pendientes: 0, completadas: 0, enseres: 0 });
  const [filterServicio, setFilterServicio] = useState("todos");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterSolicitante, setFilterSolicitante] = useState("todos");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTelefono, setShowTelefono] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterServicio !== "todos") params.tipo_servicio = filterServicio;
      if (filterEstado !== "todos") params.estado = filterEstado;
      if (filterSolicitante !== "todos") params.tipo_solicitante = filterSolicitante;
      if (search.trim()) params.search = search.trim();
      const [list, statsRes] = await Promise.all([adminListSolicitudes(params), adminStats()]);
      setSolicitudes(list);
      setStatsData(statsRes);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("sfh_admin_token");
        localStorage.removeItem("sfh_admin_session");
        navigate("/admin");
      } else {
        toast.error("No se pudieron cargar las solicitudes");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const raw = localStorage.getItem("sfh_admin_session");
    if (!raw) {
      navigate("/admin");
      return;
    }
    const parsed = JSON.parse(raw);
    if (parsed.rol === "tecnico_ayuntamiento") { navigate("/admin/ayuntamiento"); return; }
    if (parsed.rol === "punto_limpio_staff") { navigate("/admin/punto-limpio"); return; }
    setSession(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!session) return;
    loadData();
    adminMe().then((me) => setTotpEnabled(!!me.totp_enabled)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, filterServicio, filterEstado, filterSolicitante]);

  useEffect(() => {
    if (!detail) { setHistorial([]); return; }
    adminHistorialSolicitud(detail.id).then(setHistorial).catch(() => setHistorial([]));
  }, [detail]);

  const handleLogout = () => {
    localStorage.removeItem("sfh_admin_token");
    localStorage.removeItem("sfh_admin_session");
    navigate("/admin");
  };

  const handleEstadoChange = async (id, estado) => {
    try {
      await adminUpdateSolicitud(id, { estado });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? { ...s, estado } : s)));
      toast.success("Estado actualizado");
      if (detail?.id === id) {
        setDetail((prev) => ({ ...prev, estado }));
        adminHistorialSolicitud(id).then(setHistorial).catch(() => {});
      }
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filterServicio !== "todos") params.tipo_servicio = filterServicio;
      if (filterEstado !== "todos") params.estado = filterEstado;
      if (filterSolicitante !== "todos") params.tipo_solicitante = filterSolicitante;
      if (search.trim()) params.search = search.trim();
      await adminExportSolicitudes(params);
      toast.success("Exportación descargada");
    } catch {
      toast.error("No se pudo exportar el listado");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return solicitudes;
    const q = search.toLowerCase();
    return solicitudes.filter(
      (s) =>
        s.ticket_code?.toLowerCase().includes(q) ||
        s.nombre_completo?.toLowerCase().includes(q) ||
        s.direccion_completa?.toLowerCase().includes(q)
    );
  }, [solicitudes, search]);

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center p-1 shrink-0">
              <img src="/logos/escudo-ayuntamiento.png" alt="Escudo Ayuntamiento" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-heading font-semibold text-municipal text-sm leading-tight">Panel de Solicitudes</p>
              <p className="text-xs text-gray-400 leading-tight">{session.nombre}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MfaSettings enabled={totpEnabled} onChanged={() => setTotpEnabled((v) => !v)} />
            <a
              href="/documentos/manual-usuario-backoffice.pdf"
              download
              data-testid="admin-manual-download-link"
            >
              <Button variant="outline" size="sm" className="border-municipal text-municipal px-2 sm:px-3">
                <BookOpen className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Manual (PDF)</span>
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-municipal text-municipal px-2 sm:px-3" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Cerrar sesión</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total solicitudes", value: statsData.total, icon: ClipboardList, color: "text-municipal" },
            { label: "Pendientes", value: statsData.pendientes, icon: Clock, color: "text-dorado" },
            { label: "Completadas", value: statsData.completadas, icon: CheckCircle2, color: "text-valoriza-green" },
            { label: "Enseres", value: statsData.enseres, icon: Sofa, color: "text-municipal" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3" data-testid={`admin-stat-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <p className="font-heading font-bold text-xl text-municipal">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="solicitudes">
          <TabsList className="mb-4">
            <TabsTrigger value="solicitudes" data-testid="tab-solicitudes">Solicitudes</TabsTrigger>
            <TabsTrigger value="mapa" data-testid="tab-mapa">Mapa de Rutas</TabsTrigger>
            <TabsTrigger value="puntolimpio" data-testid="tab-puntolimpio">Punto Limpio</TabsTrigger>
            <TabsTrigger value="bonificacion" data-testid="tab-bonificacion">Bonificación Tasa</TabsTrigger>
            <TabsTrigger value="hacienda" data-testid="tab-hacienda">Hacienda</TabsTrigger>
            <TabsTrigger value="islas" data-testid="tab-islas">Islas de Contenedores</TabsTrigger>
          </TabsList>

          <TabsContent value="solicitudes">
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por ticket, nombre o dirección..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="admin-search-input"
                />
              </div>
              <Select value={filterServicio} onValueChange={setFilterServicio}>
                <SelectTrigger className="w-[160px]" data-testid="admin-filter-servicio">
                  <Filter className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Servicio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los servicios</SelectItem>
                  <SelectItem value="enseres">Enseres</SelectItem>
                  <SelectItem value="poda">Poda</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger className="w-[160px]" data-testid="admin-filter-estado">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  {ESTADOS.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSolicitante} onValueChange={setFilterSolicitante}>
                <SelectTrigger className="w-[170px]" data-testid="admin-filter-solicitante">
                  <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Solicitante" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Ciudadanos y empresas</SelectItem>
                  <SelectItem value="ciudadano">Solo ciudadanos</SelectItem>
                  <SelectItem value="empresa">Solo empresas</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" className="border-municipal text-municipal" onClick={handleExport} data-testid="admin-export-btn">
                <Download className="w-4 h-4 mr-2" /> Exportar Excel
              </Button>
              <Button variant="outline" className="border-municipal text-municipal" onClick={() => setShowTelefono(true)} data-testid="admin-nueva-telefono-btn">
                <Phone className="w-4 h-4 mr-2" /> Nueva solicitud telefónica
              </Button>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead className="hidden md:table-cell">Dirección</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-10">Cargando solicitudes...</TableCell>
                    </TableRow>
                  )}
                  {!loading && filtered.map((s) => (
                    <TableRow key={s.id} className="cursor-pointer hover:bg-gray-50" data-testid={`admin-row-${s.ticket_code}`}>
                      <TableCell onClick={() => setDetail(s)} className="font-medium text-municipal">{s.ticket_code}</TableCell>
                      <TableCell onClick={() => setDetail(s)}>
                        <Badge variant="outline" className="gap-1">
                          {s.tipo_servicio === "enseres" ? <Sofa className="w-3 h-3" /> : <Leaf className="w-3 h-3" />}
                          {s.tipo_servicio === "enseres" ? "Enseres" : "Poda"}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => setDetail(s)}>
                        {s.tipo_solicitante === "empresa" ? (
                          <span className="flex items-center gap-1.5">
                            {s.razon_social}
                            <Badge className="bg-municipal text-white text-[10px]">Empresa</Badge>
                          </span>
                        ) : (
                          s.nombre_completo
                        )}
                      </TableCell>
                      <TableCell onClick={() => setDetail(s)} className="hidden md:table-cell max-w-[220px] truncate">{s.direccion_completa}</TableCell>
                      <TableCell onClick={() => setDetail(s)}>{s.fecha_preferida}</TableCell>
                      <TableCell>
                        <Select value={s.estado} onValueChange={(v) => handleEstadoChange(s.id, v)}>
                          <SelectTrigger className={`w-[130px] h-8 text-xs border-0 ${ESTADO_COLORS[s.estado]}`} data-testid={`admin-estado-select-${s.ticket_code}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS.map((e) => (
                              <SelectItem key={e} value={e}>{e}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-10">No hay solicitudes que coincidan con los filtros.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="mapa">
            <AdminMapView />
          </TabsContent>

          <TabsContent value="puntolimpio">
            <PuntoLimpioPanel />
          </TabsContent>

          <TabsContent value="bonificacion">
            <BonificacionPanel />
          </TabsContent>

          <TabsContent value="hacienda">
            <HaciendaPanel />
          </TabsContent>

          <TabsContent value="islas">
            <IslasPanel />
          </TabsContent>
        </Tabs>
      </main>

      <SolicitudTelefonoDialog open={showTelefono} onClose={() => setShowTelefono(false)} onCreated={loadData} />

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-municipal font-heading">{detail.ticket_code}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  <strong className="text-municipal">Solicitante:</strong>{" "}
                  {detail.tipo_solicitante === "empresa" ? (
                    <>
                      {detail.razon_social} — CIF: {detail.cif} <Badge className="bg-municipal text-white text-[10px] ml-1">Empresa</Badge>
                      <br />
                      <span className="text-xs text-gray-500">Persona de contacto: {detail.nombre_completo}</span>
                      {detail.direccion_empresa && (
                        <>
                          <br />
                          <span className="text-xs text-gray-500">Dirección de la empresa: {detail.direccion_empresa}</span>
                        </>
                      )}
                    </>
                  ) : (
                    <>{detail.nombre_completo} — DNI/NIE: {detail.dni_solicitante}</>
                  )}
                </p>
                <p><strong className="text-municipal">Contacto:</strong> {detail.email} — {detail.telefono}</p>
                <p><strong className="text-municipal">Servicio:</strong> {detail.tipo_servicio === "enseres" ? "Enseres" : "Poda"}</p>
                {detail.enseres_lista && <p><strong className="text-municipal">Elementos:</strong> {detail.enseres_lista.join(", ")} {detail.es_mueble_grande && "(mueble de grandes dimensiones)"}</p>}
                {detail.tipo_vegetacion && <p><strong className="text-municipal">Tipo vegetación:</strong> {detail.tipo_vegetacion} ({detail.cantidad_bultos} bolsas/atillos)</p>}
                <p><strong className="text-municipal">Dirección:</strong> {detail.direccion_completa}</p>
                <p><strong className="text-municipal">Isla de contenedores:</strong> {detail.isla_contenedores}</p>
                <p><strong className="text-municipal">Fecha preferida:</strong> {detail.fecha_preferida}</p>
                <p><strong className="text-municipal">Origen:</strong> <Badge variant="outline">{detail.origen_bonificacion === "telefono" ? "Teléfono" : "Web"}</Badge></p>
                {detail.detalles && <p><strong className="text-municipal">Notas:</strong> {detail.detalles}</p>}
                <div className="pt-3 border-t border-gray-100">
                  <p className="font-semibold text-municipal mb-1">Pagador de la tasa de basuras</p>
                  <p>{detail.pagador_nombre} {detail.pagador_apellidos} — DNI/NIE: {detail.pagador_dni}</p>
                  <p>{detail.pagador_domicilio} {detail.pagador_numero}{detail.pagador_piso ? `, ${detail.pagador_piso}º` : ""} {detail.pagador_puerta || ""}</p>
                </div>
                <div className="pt-3 border-t border-gray-100" data-testid="historial-estados-section">
                  <p className="font-semibold text-municipal mb-2 flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" /> Historial de cambios de estado
                  </p>
                  {historial.length === 0 ? (
                    <p className="text-xs text-gray-400" data-testid="historial-empty">Sin cambios de estado registrados todavía.</p>
                  ) : (
                    <ul className="space-y-1.5 max-h-32 overflow-y-auto" data-testid="historial-list">
                      {historial.map((h, idx) => (
                        <li key={idx} className="text-xs text-gray-500" data-testid={`historial-row-${idx}`}>
                          <span className="text-municipal font-medium">{h.actor}</span> cambió de{" "}
                          <span className="font-medium">{h.detalle?.estado_anterior || "—"}</span> a{" "}
                          <span className="font-medium text-valoriza-green">{h.detalle?.estado_nuevo}</span>
                          {" · "}{new Date(h.fecha).toLocaleString("es-ES")}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
